/**
 * Access Denied Page Component
 *
 * Displayed when a user tries to access a page they don't have permission to view.
 * Provides options to go home, send feedback, or contact support.
 */

import { type CSSProperties, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';

const SUPPORT_EMAIL = 'dylanherthoge@outlook.com';

/**
 * Access denied page shown when user lacks permission
 */
export const AccessDeniedPage = () => {
  const navigate = useNavigate();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const handleGoHome = () => {
    navigate('/climb');
  };

  const handleOpenFeedback = () => {
    setIsFeedbackOpen(true);
  };

  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
  };

  const handleContactSupport = () => {
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Access%20Request&body=I%20was%20trying%20to%20access%3A%20${encodeURIComponent(window.location.href)}`;
  };

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Icon */}
        <div style={iconContainerStyle}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ef4444"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
        </div>

        {/* Heading */}
        <h1 style={headingStyle}>Access Denied</h1>

        {/* Message */}
        <p style={messageStyle}>You don&apos;t have permission to view this page.</p>

        {/* Action Buttons */}
        <div style={buttonContainerStyle}>
          <button onClick={handleGoHome} style={primaryButtonStyle}>
            Go to Homepage
          </button>
          <button onClick={handleOpenFeedback} style={secondaryButtonStyle}>
            Send Feedback
          </button>
        </div>

        {/* Support Link */}
        <p style={supportTextStyle}>
          Need help?{' '}
          <button onClick={handleContactSupport} style={supportLinkStyle}>
            Contact support
          </button>
        </p>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal isOpen={isFeedbackOpen} onClose={handleCloseFeedback} />
    </main>
  );
};

/**
 * Styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  backgroundColor: '#f9fafb',
};

const contentStyle: CSSProperties = {
  maxWidth: '400px',
  width: '100%',
  textAlign: 'center',
};

const iconContainerStyle: CSSProperties = {
  marginBottom: '24px',
};

const headingStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: '12px',
};

const messageStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  marginBottom: '32px',
  lineHeight: 1.5,
};

const buttonContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '24px',
};

const primaryButtonStyle: CSSProperties = {
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  color: 'white',
  backgroundColor: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
};

const secondaryButtonStyle: CSSProperties = {
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  color: '#374151',
  backgroundColor: 'white',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
};

const supportTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#6b7280',
};

const supportLinkStyle: CSSProperties = {
  color: '#3b82f6',
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  textDecoration: 'underline',
};

export default AccessDeniedPage;
